import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import UtilsUnit from "../../../utils/UtilsUnit";

export const useWidgetTerminalInfoMap = ({ nationalModelForTerminalData, selectedBusLinesData, markers, uploadedPolygon }) => {
  const [polygonGeoJson, setPolygonGeoJson] = useState(nationalModelForTerminalData.terminalGeoJSON);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const offsetAmount = 0.00001;

  // Update polygon when uploadedPolygon changes
  useEffect(() => {
    if (uploadedPolygon) {
      setPolygonGeoJson(uploadedPolygon);
    }
  }, [uploadedPolygon]);

  // Stores map reference when Leaflet map is ready
  const setMapRef = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Fits map bounds to include the terminal polygon
  const fitMapBounds = useCallback(() => {
    if (!mapRef.current) return;

    if (polygonGeoJson && polygonGeoJson.coordinates) {
      const bounds = L.latLngBounds([]);
      polygonGeoJson.coordinates.forEach((polygon) => {
        polygon.forEach((ring) => {
          ring.forEach(([lng, lat]) => {
            bounds.extend([lat, lng]);
          });
        });
      });
      mapRef.current.fitBounds(bounds);
      mapRef.current.setZoom(16);
    } else {
      const defaultCenter = UtilsUnit.getCenterPropForMap();
      mapRef.current.setView([defaultCenter.lat, defaultCenter.lng], 12);
    }
  }, [polygonGeoJson]);

  // Fits map bounds when Leaflet map is loaded
  useEffect(() => {
    if (mapRef.current) {
      fitMapBounds();
    }
  }, [fitMapBounds, polygonGeoJson]);

  // Clears selected marker when it is no longer in the markers list
  useEffect(() => {
    if (!selectedMarker) return;
    if (selectedMarker.type === "marker") {
      const markerStillExists = markers.some((marker) => marker.stop_code === selectedMarker.erezSKU);
      if (!markerStillExists) setSelectedMarker(null);
    } else if (selectedMarker.type === "polyline") {
      const polylineStillExists = selectedBusLinesData.some((line) => line.RouteShortName === selectedMarker.terminalName);
      if (!polylineStillExists) setSelectedMarker(null);
    } else if (selectedMarker.type === "polygon") {
      if (nationalModelForTerminalData.erezSKU !== selectedMarker.erezSKU) setSelectedMarker(null);
    }
  }, [markers, selectedBusLinesData, nationalModelForTerminalData, selectedMarker]);

  const handleMarkerClick = (marker) => {
    setSelectedMarker({ ...marker, type: "marker" });
  };

  const handlePolylineClick = (polyline) => {
    setSelectedMarker({ ...polyline, type: "polyline" });
  };

  const handlePolygonClick = (polygon) => {
    setSelectedMarker({ ...polygon, type: "polygon" });
  };

  const handleCloseMarkerClick = () => {
    setSelectedMarker(null);
  };

  // Handle clicks outside the map container
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mapContainerRef.current && !mapContainerRef.current.contains(event.target)) {
        setSelectedMarker(null);
      }
    };

    // Add event listener when there's a selected marker
    if (selectedMarker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedMarker]);

  // Calculates center point of a polygon by averaging all coordinates
  function getPolygonCenter(polygon) {
    let latSum = 0;
    let lngSum = 0;
    let count = 0;

    polygon.forEach((ring) => {
      ring.forEach(([lng, lat]) => {
        latSum += lat;
        lngSum += lng;
        count++;
      });
    });

    return {
      lat: latSum / count,
      lng: lngSum / count,
    };
  }

  // Applies small offsets to shared coordinates to differentiate overlapping polylines
  function offsetSharedCoordinates(allBusLines, lineIndex, offsetAmount) {
    // To track shared coordinates across different polylines
    const coordCount = {};
    const adjustedCoordinates = [];

    // First, count how many times each coordinate appears across all bus lines
    allBusLines.forEach((busLine, index) => {
      if (index !== lineIndex) {
        busLine.geojson.coordinates.forEach(([lng, lat]) => {
          const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
          coordCount[key] = (coordCount[key] || 0) + 1;
        });
      }
    });

    // Apply offsets only if a coordinate is shared with other polylines
    allBusLines[lineIndex].geojson.coordinates.forEach(([lng, lat]) => {
      const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
      const count = coordCount[key];

      if (count > 0) {
        // Apply small offset based on lineIndex to differentiate polylines sharing the same coordinate
        const offsetX = offsetAmount * lineIndex;
        const offsetY = offsetAmount * lineIndex;

        adjustedCoordinates.push([lng + offsetX, lat + offsetY]);
      } else {
        // If not shared, keep the original coordinate
        adjustedCoordinates.push([lng, lat]);
      }
    });

    return adjustedCoordinates;
  }

  return {
    offsetSharedCoordinates,
    offsetAmount,
    handlePolylineClick,
    polygonGeoJson,
    getPolygonCenter,
    handlePolygonClick,
    setMapRef,
    fitMapBounds,
    handleCloseMarkerClick,
    handleMarkerClick,
    selectedMarker,
    mapContainerRef,
  };
};
